import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  ajvResolver,
  SidePanelLoader,
  responseErrorsResolver,
  ResponseError,
  PermissionEnum,
  Popup,
  PermissionDenied,
  usePrevious,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearBankAndPspItemFormState } from './store/slices/item-form';
import ItemForm from '../../components/bank-and-psp/item-form';
import {
  ExportFileTypeEnum,
  PspPartnerFormValues,
  PspPartnerItem,
  PspPartnerPaymentType,
} from './types';
import {
  useCreatePspPartnerMutation,
  useLazyGetPspPartnerQuery,
  useUpdatePspPartnerMutation,
} from './store/services';
import { createSchema, updateSchema, sortCodeRegexp } from './variables/schema';

import { BankRoutes } from '../../routes';

const BankAndPspItemForm = () => {
  const { bankId } = useParams();

  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [sortCode, setSortCode] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [formattedSchema, setFormattedSchema] = useState(
    bankId ? updateSchema : createSchema,
  );

  const {
    bankAndPsp: {
      itemForm: { formValues },
      info: { generalInfo, casp },
    },
    configurationDictionaries: { countries, activePermissions, paymentTypes },
  } = useAppSelector((state) => state);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
    setError,
    setValue,
  } = useForm<PspPartnerFormValues>({
    resolver: ajvResolver(formattedSchema),
    defaultValues: {
      active: false,
      paymentsTypes: [],
    },
  });

  const itemType = watch('partnerType');
  const paymentTypesFields = watch('paymentsTypes');
  const itemLocation = watch('location');
  const sortCodeValue = watch('sortCode');
  const caspId = watch('caspId');
  const aba = watch('aba');
  const prevSortCode = usePrevious(sortCode);
  const prevPaymentTypesFields = usePrevious(paymentTypesFields);

  const [create, { isLoading: requestInProgress, isSuccess: createIsSuccess }] =
    useCreatePspPartnerMutation();
  const [update, { isLoading: updateInProgress, isSuccess: updateIsSuccess }] =
    useUpdatePspPartnerMutation();
  const [getPartner, { isLoading, isFetching }] = useLazyGetPspPartnerQuery();

  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onFormSubmit = handleSubmit(async (values) => {
    let response;

    setGlobalError('');

    const nextValues: PspPartnerFormValues = {
      partnerType: values.partnerType,
      location: values.location,
      countryId: values.countryId,
      companyName: values.companyName,
      legalName: values.legalName,
      address: values.address,
      managerContacts: values.managerContacts,
      onlineBankLink: values.onlineBankLink,
      active: values.active,
      paymentsTypes: values.paymentsTypes,
    };

    if (values.swift) {
      nextValues.swift = values.swift;
    }

    if (values.aba) {
      nextValues.aba = values.aba;
    }

    if (values.description) {
      nextValues.description = values.description;
    }

    if (values.sortCode) {
      nextValues.sortCode = values.sortCode;
    }

    if (values.intermediaryBankName) {
      nextValues.intermediaryBankName = values.intermediaryBankName;
    }

    if (values.intermediaryBankSwift) {
      nextValues.intermediaryBankSwift = values.intermediaryBankSwift;
    }

    if (values.accountOfCorrespondentWithIntermediaryBank) {
      nextValues.accountOfCorrespondentWithIntermediaryBank =
        values.accountOfCorrespondentWithIntermediaryBank;
    }

    if (values.beneficiary) {
      nextValues.beneficiary = values.beneficiary;
    }

    if (values.caspId) {
      nextValues.caspId = values.caspId;
    }

    if (formValues?.caspId && !values.caspId) {
      nextValues.caspId = null;
    }

    if (values.exportFileType === ExportFileTypeEnum.NULL) {
      nextValues.exportFileType = null;
    } else {
      nextValues.exportFileType = values.exportFileType;
    }

    if (bankId) {
      if (values.pspIntegrationId) {
        nextValues.pspIntegrationId = values.pspIntegrationId;
      }

      nextValues.visibleToClients = values.visibleToClients;

      response = (await update({
        body: nextValues,
        id: bankId,
      })) as {
        data?: PspPartnerItem;
        error: ResponseError;
      };
    } else {
      nextValues.visibleToClients = true;

      response = (await create(nextValues)) as {
        data?: PspPartnerItem;
        error: ResponseError;
      };
    }

    if (response?.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  });

  const getPartnerAction = useCallback(() => {
    getPartner(bankId as string);
  }, [bankId]);

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);

    if (bankId) {
      setTimeout(() => {
        navigate(
          generatePath(BankRoutes.BANK_AND_PSP_GENERAL, {
            bankId,
          }),
        );
      }, 100);
    } else {
      setTimeout(() => {
        navigate(`${BankRoutes.BANK_AND_PSP_LIST}${search || ''}`);
      }, 100);
    }
  }, [bankId]);

  useEffect(() => {
    if (bankId) {
      let nextSchema: any = updateSchema;

      if (sortCodeValue) {
        nextSchema = {
          ...updateSchema,
          properties: {
            ...updateSchema.properties,
            sortCode: {
              type: 'string',
              regexp: sortCodeRegexp.toString(),
              errorMessage: 'Sort Code is incorrect',
            },
          },
          required: [...updateSchema.required, 'sortCode'],
        };
      } else if (prevSortCode && !sortCodeValue) {
        nextSchema = updateSchema;
      }

      setFormattedSchema(nextSchema);
    } else {
      let nextSchema: any = createSchema;

      if (sortCodeValue) {
        nextSchema = {
          ...createSchema,
          properties: {
            ...createSchema.properties,
            sortCode: {
              type: 'string',
              regexp: sortCodeRegexp.toString(),
              errorMessage: 'Sort Code is incorrect',
            },
          },
          required: [...createSchema.required, 'sortCode'],
        };
      } else if (prevSortCode && !sortCodeValue) {
        nextSchema = createSchema;
      }

      setFormattedSchema(nextSchema);
    }
  }, [sortCodeValue, prevSortCode, bankId]);

  useEffect(() => {
    if (createIsSuccess || updateIsSuccess) {
      handleClose();
    }
  }, [createIsSuccess, updateIsSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (bankId) {
      getPartnerAction();
    }

    return () => {
      dispatch(clearBankAndPspItemFormState());
    };
  }, [dispatch, bankId, getPartnerAction]);

  useEffect(() => {
    if (formValues && bankId) {
      reset(formValues);

      if (formValues.sortCode) {
        setSortCode(formValues.sortCode);
      }
    }
  }, [reset, formValues, bankId]);

  useEffect(() => {
    if (
      !paymentTypesFields?.includes(PspPartnerPaymentType.FEDWIRE) &&
      prevPaymentTypesFields?.includes(PspPartnerPaymentType.FEDWIRE)
    ) {
      if (!bankId && aba) {
        setValue('aba', '');
      } else if (bankId && aba && !generalInfo?.aba) {
        setValue('aba', '');
      }
    }

    if (
      !paymentTypesFields?.includes(
        PspPartnerPaymentType.CRYPTO_ASSET_PAYMENT,
      ) &&
      prevPaymentTypesFields?.includes(
        PspPartnerPaymentType.CRYPTO_ASSET_PAYMENT,
      )
    ) {
      if (!bankId && caspId) {
        setValue('caspId', '');
      } else if (bankId && caspId && !generalInfo?.caspId) {
        setValue('caspId', '');
      }
    }
  }, [paymentTypesFields, prevPaymentTypesFields, aba, caspId, bankId]);

  let content = (
    <ItemForm
      control={control}
      errors={errors}
      itemLocation={itemLocation}
      itemType={itemType}
      countries={countries}
      itemId={bankId}
      isEditable={activePermissions?.includes(PermissionEnum.PSP_EDIT_ACTION)}
      pspIntegration={generalInfo?.pspIntegration}
      caspAvailable={Boolean(
        paymentTypesFields?.includes(
          PspPartnerPaymentType.CRYPTO_ASSET_PAYMENT,
        ),
      )}
      abaAvailable={Boolean(
        paymentTypesFields?.includes(PspPartnerPaymentType.FEDWIRE),
      )}
      aba={generalInfo?.aba}
      caspId={generalInfo?.caspId}
      setSortCode={setSortCode}
      sortCode={sortCode}
      setValue={setValue}
      paymentTypes={paymentTypes}
      casp={casp}
    />
  );

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else if (
    !isLoading &&
    !isFetching &&
    ((!bankId && !activePermissions?.includes(PermissionEnum.PSP_ADD_ACTION)) ||
      (bankId && !activePermissions?.includes(PermissionEnum.PSP_EDIT_ACTION)))
  ) {
    content = <PermissionDenied />;
  }

  let buttonText = 'Add Bank';

  if (bankId) {
    buttonText = 'Save changes';
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={bankId ? 'Bank details' : 'Add Bank'}
        errorSidepanel={globalError}
        sidepanelExpandedKey="bank-details"
        footer={
          <>
            {(activePermissions?.includes(PermissionEnum.PSP_EDIT_ACTION) ||
              (!bankId &&
                activePermissions?.includes(
                  PermissionEnum.PSP_ADD_ACTION,
                ))) && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : buttonText
                }
                onClick={onFormSubmit}
                bold
                disabled={requestInProgress || updateInProgress}
              />
            )}

            <ButtonSecondary
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default BankAndPspItemForm;
